import React from 'react';

import { GatherUsingAINodeState, NodeField, SpeakTextNodeState } from '@components/Flow/Nodes';
import { Command, tabActions } from '@store/index';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Label } from '@common/Label';
import { TTSVoiceAutocomplete } from '@common/Autocomplete/TTSVoiceAutocomplete';
import { useQuery } from '@tanstack/react-query';
import { getTTSVoiceParams, TTSVoice } from '@requests/telnyx';
import { getTTSVoices } from '@requests/telnyx/tts-voices.requests';
import { RequestError } from '@requests/common';

interface Props {
  command: Command;
  fields: NodeField[];
}

export const PollyVoice: React.FC<Props> = ({ command, fields }) => {
  const dispatch = useDispatch();

  const supportedCommands = ['call.ai_gather', 'call.speak'];
  if (!supportedCommands.includes(command?.config?.command_id)) {
    throw new Error(
      'This node does not support using the AWS Polly voice. The PollyVoice component should be updated to allow it.'
    );
  }

  const { data: options = [], isLoading } = useQuery<TTSVoice[], RequestError, TTSVoice[], [string, getTTSVoiceParams]>(
    {
      queryKey: ['tts-voices', { provider: 'aws' }],
      queryFn: getTTSVoices,
      retry: false,
    }
  );

  const awsVoiceOptions = options.filter((item) => item.provider === 'aws');

  const state = command.state as SpeakTextNodeState | GatherUsingAINodeState;
  const isAWSVoice = state?.voice?.value === 'AWS.Polly';
  if (!isAWSVoice) return null;

  const polly_voice_type = state.aws_polly_voice_id;
  const polly_voice_neural = state.aws_polly_neural; // handles the type of voice (standard, neural, generative, long_form)

  const pollyVoiceType = fields.find((field) => field.id === 'aws_polly_voice_id');
  const pollyVoiceNeural = fields.find((field) => field.id === 'aws_polly_neural');

  const updateField = (value: string, fieldId: string) => {
    if (!pollyVoiceType) return;

    const nodeId = command.node.id;

    dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId, value }));
    dispatch(tabActions.setSaved(false));

    if (fieldId === 'aws_polly_voice_id') {
      const newSelectedVoice = awsVoiceOptions.find((item) => item.value === value);
      const newSelectedNeural = newSelectedVoice?.neural;
      const newSelectedStandard = newSelectedVoice?.standard;
      const newSelectedGenerative = newSelectedVoice?.generative;
      const newSelectedLongForm = newSelectedVoice?.long_form;

      if (selectedVoice && pollyVoiceNeural) {
        if (newSelectedLongForm) {
          dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId: pollyVoiceNeural.id, value: 'long_form' }));
        }

        if (newSelectedGenerative) {
          dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId: pollyVoiceNeural.id, value: 'generative' }));
        }

        if (newSelectedNeural) {
          dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId: pollyVoiceNeural.id, value: 'neural' }));
        }

        if (newSelectedStandard) {
          dispatch(tabActions.nodeInputUpdate({ nodeId, fieldId: pollyVoiceNeural.id, value: 'standard' }));
        }
      }
    }
  };

  const selectedVoice = awsVoiceOptions.find((item) => item.value === polly_voice_type);

  const selectedNeural = selectedVoice?.neural;
  const selectedStandard = selectedVoice?.standard;
  const selectedGenerative = selectedVoice?.generative;
  const selectedLongForm = selectedVoice?.long_form;

  return (
    <Stack spacing='1.25rem'>
      {pollyVoiceType && (
        <Stack spacing={1}>
          <Label title={pollyVoiceType?.label} tooltip={pollyVoiceType?.description} />
          <TTSVoiceAutocomplete
            provider='aws'
            value={polly_voice_type}
            onChange={(_, newValue) => newValue && updateField(newValue.value, pollyVoiceType.id)}
            placeholder='AWS Voice ID'
          />
        </Stack>
      )}
      {pollyVoiceNeural && selectedVoice && (
        <Stack spacing={1}>
          <Label title={pollyVoiceNeural?.label} tooltip={pollyVoiceNeural?.description} />
          <ToggleButtonGroup
            size='small'
            color='primary'
            value={polly_voice_neural}
            exclusive
            onChange={(_, newVal) =>
              newVal !== undefined && newVal !== null && updateField(newVal, pollyVoiceNeural.id)
            }
            fullWidth
          >
            <ToggleButton size='small' value='standard' disabled={!selectedStandard || isLoading}>
              Standard
            </ToggleButton>
            <ToggleButton size='small' value='neural' disabled={!selectedNeural || isLoading}>
              Neural
            </ToggleButton>
            <ToggleButton size='small' value='generative' disabled={!selectedGenerative || isLoading}>
              Generative
            </ToggleButton>
            <ToggleButton size='small' value='long_form' disabled={!selectedLongForm || isLoading}>
              Long Form
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
    </Stack>
  );
};
